import { TIMESTAMP_MODE } from "@/types/tables/ConversationsTable"

import type { SavedViewDisplay } from "../types/savedViews"
import type { SortDescriptor } from "../types/sortDescriptor"
import {
  normalizeSortColumn,
  normalizeVisibleColumns,
} from "./normalizeLegacyViewConfig"

interface NormalizedDisplayConfig {
  sortDescriptor: SortDescriptor
  visibleColumns: string[] | null
}

/**
 * Normalize a saved view's display config, applying defaults for any
 * missing fields. Handles stale auto-saved state that predates newer
 * display fields and removes discontinued custom metadata columns.
 *
 * Returns null for visibleColumns when not present in the config, so callers
 * can decide whether to apply column changes.
 */
export function normalizeViewDisplayConfig(
  display: SavedViewDisplay | undefined,
): NormalizedDisplayConfig {
  // Treat empty visibleColumns as "no preference" — stale auto-saved state
  // from before column persistence was added can write visibleColumns: []
  const hasVisibleColumns =
    display?.visibleColumns && display.visibleColumns.length > 0
  const retainedVisibleColumns = hasVisibleColumns
    ? normalizeVisibleColumns(display.visibleColumns!).filter(
        (column) => !column.startsWith("metadata_"),
      )
    : null
  const normalizedVisibleColumns =
    retainedVisibleColumns && retainedVisibleColumns.length > 0
      ? retainedVisibleColumns
      : null

  // Legacy views represented the selected timestamp mode with one column.
  // Once either timestamp was visible, migrate that preference to both real
  // Uploaded/Created columns. New views omit timestampMode and preserve the
  // user's individual visibility choices.
  const visibleColumns = normalizedVisibleColumns
    ? Array.from(
        new Set([
          ...normalizedVisibleColumns,
          ...(display?.timestampMode &&
          normalizedVisibleColumns.some((column) =>
            ["created_at", "occurred_at"].includes(column),
          )
            ? ["created_at", "occurred_at"]
            : []),
        ]),
      )
    : null

  return {
    sortDescriptor: {
      column: display?.sortColumn
        ? normalizeSortColumn(display.sortColumn)
        : TIMESTAMP_MODE.CREATED_AT,
      direction:
        display?.sortDirection === "ascending" ? "ascending" : "descending",
    },
    visibleColumns,
  }
}
